import { useEffect, useState } from "react";
import api, { apiErr, API } from "@/lib/api";
import { toast } from "sonner";
import { useActivity } from "@/context/ActivityContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import { InlineError } from "@/components/InlineError";
import { setPin as savePin, hasPin, clearPin, pinScope } from "@/lib/pin";
import { useAuth } from "@/context/AuthContext";
import { Save, ImagePlus, Building2, Lock } from "lucide-react";
import { SensitiveActionsCard } from "@/components/SensitiveActionsConfig";

const CURRENCIES = [
  ["KMF", "Franc comorien (KMF)"], ["XOF", "Franc CFA Ouest (XOF)"], ["XAF", "Franc CFA Centre (XAF)"],
  ["CDF", "Franc congolais (CDF)"], ["GNF", "Franc guinéen (GNF)"], ["EUR", "Euro (EUR)"],
  ["USD", "Dollar US (USD)"], ["MAD", "Dirham marocain (MAD)"], ["NGN", "Naira (NGN)"], ["GHS", "Cedi (GHS)"],
];

const TIMEZONES = [
  ["Indian/Comoro", "Comores (UTC+3)"],
  ["Indian/Antananarivo", "Madagascar (UTC+3)"],
  ["Africa/Nairobi", "Afrique de l'Est (UTC+3)"],
  ["Africa/Kinshasa", "Kinshasa / Ouest RDC (UTC+1)"],
  ["Africa/Lubumbashi", "Est RDC (UTC+2)"],
  ["Africa/Lagos", "Afrique de l'Ouest (UTC+1)"],
  ["Africa/Abidjan", "Abidjan / GMT (UTC+0)"],
  ["Africa/Casablanca", "Maroc (UTC+1)"],
  ["Europe/Paris", "Paris (UTC+1/+2)"],
  ["UTC", "UTC (temps universel)"],
];

const TEMPLATE_FIELDS = [
  ["invoice_template", "Modèle de facture (WhatsApp)", "Variables : {number} {client} {lines} {total} {paid} {balance} {currency}"],
  ["delivery_template", "Modèle de rappel de livraison", "Variables : {client} {item} {quantity} {unit} {date} {address} {org}"],
  ["return_template", "Modèle de rappel de retour de location", "Variables : {client} {number} {date} {org}"],
  ["relance_template", "Modèle de relance de créance", "Variables : {client} {balance} {count} {org} {currency}"],
];

const COMPANY_FIELDS = [
  ["company_address", "Adresse", "col-span-2"],
  ["company_city", "Ville", ""],
  ["company_country", "Pays", ""],
  ["company_phone", "Téléphone", ""],
  ["company_email", "E-mail", ""],
  ["company_website", "Site web", ""],
  ["company_rccm", "RCCM / Registre du commerce", ""],
  ["company_nif", "NIF / Identifiant fiscal", ""],
  ["tax_rate", "TVA (%)", ""],
  ["max_discount_percent", "Remise maximum autorisée (%)", ""],
];

export default function SettingsPage() {
  const { reloadSettings, reload } = useActivity();
  const [form, setForm] = useState(null);
  const [savingC, setSavingC] = useState(false);
  const [companyErr, setCompanyErr] = useState("");
  const [applyCurAll, setApplyCurAll] = useState(true);
  const [confirmCompany, setConfirmCompany] = useState(false);
  const [savingT, setSavingT] = useState(false);
  const [tplErr, setTplErr] = useState("");

  useEffect(() => { api.get("/settings").then((r) => setForm(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setForm((x) => ({ ...x, [k]: v }));

  const saveCompany = async () => {
    setSavingC(true); setCompanyErr("");
    try {
      const payload = { org_name: form.org_name, currency: form.currency, timezone: form.timezone || "Indian/Comoro" };
      COMPANY_FIELDS.forEach(([k]) => { payload[k] = (k === "tax_rate" || k === "max_discount_percent") ? Number(form[k] || 0) : (form[k] || ""); });
      await api.post(`/settings?apply_currency_to_activities=${applyCurAll}`, payload);
      toast.success("Informations de l'entreprise enregistrées");
      setConfirmCompany(false);
      reloadSettings && reloadSettings();
      reload && reload();
    } catch (e) { setConfirmCompany(false); setCompanyErr(apiErr(e.response?.data?.detail, e)); }
    finally { setSavingC(false); }
  };

  const saveTemplates = async () => {
    setSavingT(true); setTplErr("");
    try {
      const payload = {};
      TEMPLATE_FIELDS.forEach(([k]) => { payload[k] = form[k] || ""; });
      await api.put("/settings", payload);
      toast.success("Modèles de messages enregistrés");
      reloadSettings && reloadSettings();
    } catch (e) { setTplErr(apiErr(e.response?.data?.detail, e)); }
    finally { setSavingT(false); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/settings", { logo_path: data.path });
      set("logo_path", data.path);
      reloadSettings && reloadSettings();
      toast.success("Logo enregistré (apparaîtra sur les factures PDF)");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  if (!form) return <div className="text-[#64748B]">Chargement…</div>;

  return (
    <div className="space-y-5 max-w-3xl">
      <div><h1 className="text-2xl md:text-3xl font-bold font-head text-[#16324F] tracking-tight">Paramètres</h1>
        <p className="text-sm text-[#64748B] mt-1">Identité de l'entreprise, devise et modèles de messages · les modules se configurent par activité (menu Activités)</p></div>

      <div className="card p-5 space-y-4">
        <div className="flex items-center gap-2 text-[#16324F] font-semibold font-head"><Building2 className="w-5 h-5" /> Identité de l'entreprise</div>

        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-center overflow-hidden">
            {form.logo_path ? <img src={`${API}/files/${form.logo_path}`} alt="logo" className="w-full h-full object-contain" /> : <ImagePlus className="w-6 h-6 text-[#94A3B8]" />}
          </div>
          <label className="btn-outline cursor-pointer" data-testid="logo-upload-btn">
            <ImagePlus className="w-4 h-4" /> {form.logo_path ? "Changer le logo" : "Téléverser un logo"}
            <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-input" />
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><label className="lbl">Nom de l'entreprise</label>
            <input className="inp" value={form.org_name || ""} onChange={(e) => set("org_name", e.target.value)} data-testid="company-name-input" /></div>
          <div><label className="lbl">Devise</label>
            <select className="inp" value={form.currency || "XOF"} onChange={(e) => set("currency", e.target.value)} data-testid="currency-select">
              {CURRENCIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div><label className="lbl">Fuseau horaire</label>
            <select className="inp" value={form.timezone || "Indian/Comoro"} onChange={(e) => set("timezone", e.target.value)} data-testid="timezone-select">
              {TIMEZONES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <p className="text-[11px] text-[#94A3B8] mt-1">Appliqué aux heures affichées dans l'appli, les reçus, les PDF, les exports et les rapports.</p>
          </div>
          <label className="sm:col-span-2 flex items-start gap-2 text-sm text-[#475569]">
            <input type="checkbox" className="mt-0.5" checked={applyCurAll} onChange={(e) => setApplyCurAll(e.target.checked)} data-testid="apply-currency-all-checkbox" />
            <span>Appliquer cette devise à toutes mes activités<span className="block text-[11px] text-[#94A3B8]">Recommandé si vous utilisez une seule monnaie. Décochez pour conserver une devise différente par activité (elle se règle alors dans le menu Activités).</span></span>
          </label>
          {COMPANY_FIELDS.map(([k, label, cls]) => (
            <div key={k} className={cls === "col-span-2" ? "sm:col-span-2" : ""}>
              <label className="lbl">{label}</label>
              <input className="inp" value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`${k.replace("_", "-")}-input`} />
            </div>
          ))}
        </div>
        <button onClick={() => { setCompanyErr(""); setConfirmCompany(true); }} disabled={savingC} className="btn-primary" data-testid="save-company-btn"><Save className="w-4 h-4" /> Enregistrer l'entreprise</button>
        <InlineError message={companyErr} title="Enregistrement impossible" />
      </div>

      <div className="card p-5 space-y-4">
        <div className="text-[#16324F] font-semibold font-head">Modèles de messages</div>
        {TEMPLATE_FIELDS.map(([k, label, help]) => (
          <div key={k}>
            <label className="lbl">{label}</label>
            <textarea className="inp min-h-[110px] font-mono text-xs" value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`settings-${k}`} />
            <p className="text-xs text-[#94A3B8] mt-1">{help}</p>
          </div>
        ))}
        <button onClick={saveTemplates} disabled={savingT} className="btn-primary" data-testid="save-settings-btn"><Save className="w-4 h-4" /> Enregistrer les modèles</button>
        <InlineError message={tplErr} title="Enregistrement impossible" />
      </div>

      <SensitiveActionsCard />

      <OfflinePinCard />

      <ConfirmDialog open={confirmCompany} danger={false} title="Confirmer l'enregistrement" message={`Enregistrer les informations de l'entreprise${form?.currency ? ` (devise : ${form.currency})` : ""} ?`} confirmLabel="Valider et enregistrer" loading={savingC} onConfirm={saveCompany} onClose={() => setConfirmCompany(false)} />
    </div>
  );
}

function OfflinePinCard() {
  const { user } = useAuth();
  const scope = pinScope(user);
  const [val, setVal] = useState("");
  const [pinSet, setPinSet] = useState(false);
  useEffect(() => { setPinSet(hasPin(scope)); }, [scope]);
  const save = async () => {
    if (!scope) { toast.error("Connectez-vous pour définir un code PIN."); return; }
    if (val.length < 4) { toast.error("Le code PIN doit comporter au moins 4 chiffres."); return; }
    await savePin(val, scope); setPinSet(true); setVal(""); toast.success("Code PIN hors-ligne enregistré sur cet appareil.");
  };
  const remove = () => { clearPin(scope); setPinSet(false); setVal(""); toast.success("Code PIN supprimé."); };
  return (
    <div className="card p-5 space-y-4" data-testid="offline-pin-card">
      <div className="flex items-center gap-2 text-[#16324F] font-semibold font-head"><Lock className="w-4 h-4" /> Sécurité hors-ligne — Code PIN</div>
      <p className="text-sm text-[#64748B]">Définissez un code à 4 chiffres pour rouvrir l'application <b>hors connexion</b> (sans internet) sur cet appareil. Il déverrouille l'accès pour continuer à vendre et encaisser. Chaque vendeur a son propre code sur son propre téléphone.</p>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="lbl">{pinSet ? "Nouveau code" : "Code PIN"}</label>
          <input type="password" inputMode="numeric" className="inp text-center tracking-[0.4em] max-w-[160px]" value={val} onChange={(e) => setVal(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••" data-testid="settings-pin-input" />
        </div>
        <button onClick={save} className="btn-primary" data-testid="settings-pin-save"><Lock className="w-4 h-4" /> {pinSet ? "Modifier le code" : "Activer le code"}</button>
        {pinSet && <button onClick={remove} className="btn-remove" data-testid="settings-pin-remove">Supprimer</button>}
        {pinSet && <span className="chip-green" data-testid="settings-pin-status">Code PIN actif ✓</span>}
      </div>
      <div className="rounded-xl bg-[#16324F]/[0.04] border border-[#16324F]/10 p-3 text-xs text-[#334155] leading-relaxed">
        <b>Comment l'utiliser :</b> 1) Ici (en ligne), activez le code. 2) Hors connexion, gardez l'application ouverte pour continuer, ou rouvrez-la : l'écran « Déverrouiller Biashara » demandera ce code. 3) Au retour du réseau, tout se synchronise automatiquement.
      </div>
    </div>
  );
}
